import { useContext } from 'react';

import { PAGE_KEY_TITLES } from '../constants/pagekeyTitles';
import CreditContext from '../context/credits/context';
import { PageKey } from '../enums/pageKey';

import AlertModal from './AlertModal';

interface Props {
  isOpen: boolean;
  pageKey: PageKey;
  creditCost: number;
  onProceed: () => void;
  onCancel: () => void;
  onRedirectToTopup?: () => void;
}

const GeneratePremiumConfirmationModal = (props: Props) => {
  const {
    isOpen,
    pageKey,
    creditCost,
    onCancel,
    onProceed,
    onRedirectToTopup,
  } = props;
  const title = PAGE_KEY_TITLES[pageKey].pageTitle;

  const { context: creditContext } = useContext(CreditContext);
  const { userCredit } = creditContext;

  const isCreditInsufficient = creditCost > userCredit.paidCredits;
  const confirmButtonText = isCreditInsufficient
    ? `Top up to prepare ${title}`
    : 'Proceed';

  const bodyText = isCreditInsufficient ? (
    <>
      You have
      <strong>
        {' '}
        {userCredit.paidCredits ?? 0} premium credit
        {userCredit.paidCredits !== 1 ? 's ' : ' '}left.
      </strong>
      You need
      <strong> {creditCost} premium credits </strong>
      to prepare {title}. Would you like to top up?
    </>
  ) : (
    <>
      You have
      <strong> {userCredit.paidCredits} premium credits left. </strong>
      {title} will{' '}
      <strong>use {creditCost} premium credits to prepare. </strong>
      Do you want to proceed?
    </>
  );

  const titleText = isCreditInsufficient
    ? `Insufficient Credits`
    : `Generate ${title}`;

  const handleConfirm = () => {
    if (isCreditInsufficient && onRedirectToTopup) {
      onRedirectToTopup();
      return;
    }

    if (!isCreditInsufficient) {
      onProceed();
    }
  };

  return (
    <AlertModal
      bodyText={bodyText}
      confirmButtonText={confirmButtonText}
      directToTopup={isCreditInsufficient}
      isOpen={isOpen}
      titleText={titleText}
      variant={isCreditInsufficient ? 'error' : 'info'}
      onCancel={onCancel}
      onConfirm={handleConfirm}
    />
  );
};

export default GeneratePremiumConfirmationModal;
